import { Switch } from '@thanx/uikit/switch'
import { useStyletron } from '@thanx/uikit/theme'
import RewardPreviewCard from 'components/RewardPreviewCard'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import { Fields as PointsExperience } from 'models/PointsExperience'
import React from 'react'
import { I18n } from 'react-redux-i18n'
import {
  getMinMaxVisibility,
  getRedeemTemplateType,
} from 'scenes/RedeemManager/helpers'
import { useFormModel } from '../../FormModel'
import { Card } from '../index'
import PreviewCard from './PreviewCard'

type Props = {
  currentCard: Card
  isExperience: boolean
  templateId: number
  pointsExperience?: PointsExperience | null
}

const t = buildTranslate('redeem_manager.builder.preview')

const Preview: React.FC<Props> = ({
  currentCard,
  isExperience,
  templateId,
  pointsExperience,
}) => {
  const {
    description,
    typeItem,
    availableOnline,
    availableInstore,
    availableCustomerSupport,
    discount,
    minimum,
    maximum,
    priceCap,
    multiUseRewards,
    discountedProductIds,
    requiredProductIds,
    instoreRedemptionType,
    numberOfPoints,
  } = useFormModel().model
  const templateType = typeItem
    ? getRedeemTemplateType(typeItem, instoreRedemptionType)
    : null
  const isDeliveryDiscount = typeItem?.key === 'delivery_discount'
  // Delivery-discount writes the configured value to either `discount`
  // (amount/percent modes) or `priceCap` (fixed-price mode). A priceCap of '0'
  // is a valid configuration — free delivery — so any set priceCap counts, not
  // only a positive one.
  const priceCapConfigured = priceCap != null && String(priceCap).trim() !== ''
  const isDiscountConfigured = isDeliveryDiscount
    ? Number(discount) > 0 || priceCapConfigured
    : Number(discount) > 0

  const onlineRedemptionMessages = [
    t(availableOnline ? 'online_available' : 'online_not_available'),
  ]
  if (availableOnline) {
    if (requiredProductIds.length > 0) {
      onlineRedemptionMessages.push(
        t('must_buy', {
          count: requiredProductIds.length,
        })
      )
    }

    if (discountedProductIds.length > 0) {
      onlineRedemptionMessages.push(
        t('limited_to', {
          count: discountedProductIds.length,
        })
      )
    }
  }

  const [css] = useStyletron()
  const isBonusPoints = templateType === 'points/static'
  const multiUseRewardsEnabled = useFlag('multi_use_rewards_ui', false)
  const templateMinMaxEnabled = useFlag('mx-template-min-max', false)
  const isDollarOff = !!templateType?.endsWith('/amount')
  const { showMinimumSpend, showMaximumDiscount } = getMinMaxVisibility({
    templateMinMaxEnabled,
    isDeliveryDiscount,
    isBonusPoints,
    isExperience,
    isDollarOff,
  })

  const minNum = minimum !== null && minimum !== '' ? Number(minimum) : null
  const maxNum = maximum !== null && maximum !== '' ? Number(maximum) : null
  const hasMinimum = minNum !== null && !Number.isNaN(minNum) && minNum > 0
  const hasMaximum = maxNum !== null && !Number.isNaN(maxNum) && maxNum > 0

  return (
    <div
      className={`${css({
        top: '146px',
        maxWidth: '332px',
      })} position-sticky ml-auto`}
    >
      <p className="small-caps-3 bold">{t('title')}</p>
      <RewardPreviewCard description={description} templateId={templateId} />
      <p className="small-caps-3 bold">{t('summary')}</p>
      <div className="card overflow-hidden">
        <Switch
          condition={!isBonusPoints}
          fallback={
            <PreviewCard
              title={t('number_of_bonus_points')}
              isConfigured={Number(numberOfPoints) > 0}
              isDisabled={false}
              messages={[
                I18n.t(`redeem_manager.types.${typeItem?.key}.preview`, {
                  points: numberOfPoints,
                  currencyName: pointsExperience?.currency_name_plural,
                }),
              ]}
            />
          }
        >
          <Switch
            condition={templateType === 'manual/item' || isExperience}
            fallback={
              <PreviewCard
                title={t('discount')}
                isConfigured={isDiscountConfigured}
                isDisabled={false}
                messages={[
                  I18n.t(`redeem_manager.types.${typeItem?.key}.preview`, {
                    discount,
                  }),
                ]}
              />
            }
          >
            <PreviewCard
              title={t('reward_type')}
              isConfigured
              isDisabled={false}
              messages={[
                I18n.t(`redeem_manager.types.${typeItem?.key}.preview`),
              ]}
            />
          </Switch>
        </Switch>
        <Switch condition={!isBonusPoints}>
          <PreviewCard
            title={t('online_redemption')}
            isConfigured={currentCard >= Card.ONLINE_REDEMPTION}
            isDisabled={!availableOnline}
            messages={onlineRedemptionMessages}
          />
          <PreviewCard
            title={t('instore_redemption')}
            isConfigured={
              isDeliveryDiscount || currentCard >= Card.INSTORE_REDEMPTION
            }
            isDisabled={isDeliveryDiscount || !availableInstore}
            messages={[
              t(
                isDeliveryDiscount || !availableInstore
                  ? 'instore_not_available'
                  : 'instore_available'
              ),
            ]}
          />
        </Switch>
        <Switch condition={showMinimumSpend}>
          <PreviewCard
            title={t('minimum_spend')}
            isConfigured={hasMinimum}
            isDisabled={!hasMinimum}
            messages={[
              hasMinimum
                ? t('minimum_spend_must_spend', { amount: minNum })
                : t('no_restriction'),
            ]}
          />
        </Switch>
        <Switch condition={showMaximumDiscount}>
          <PreviewCard
            title={t('maximum_discount')}
            isConfigured={hasMaximum}
            isDisabled={!hasMaximum}
            messages={[
              hasMaximum
                ? t('maximum_discount_cannot_redeem', { amount: maxNum })
                : t('no_restriction'),
            ]}
          />
        </Switch>
        <Switch condition={multiUseRewardsEnabled}>
          <PreviewCard
            title={t('number_of_uses')}
            isConfigured={currentCard >= Card.MULTI_USE_REWARDS}
            isDisabled={!multiUseRewards}
            messages={[
              t(
                multiUseRewards?.usageType === 'exactly_once'
                  ? 'exactly_once'
                  : 'multiple_times'
              ),
            ]}
          />
        </Switch>
        <PreviewCard
          title={t('customer_support')}
          isConfigured={currentCard >= Card.CUSTOMER_SUPPORT || isBonusPoints}
          isDisabled={!availableCustomerSupport}
          messages={[
            t(
              availableCustomerSupport
                ? 'support_available'
                : 'support_not_available'
            ),
          ]}
        />
      </div>
    </div>
  )
}

export default Preview
